'use client';

import { type InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import type { SerializedEditorState } from 'lexical';
import { useEffect, useRef } from 'react';

import { editorTheme } from '../../editor/themes/editor-theme';
import { nodes } from './nodes';

const viewerConfig: InitialConfigType = {
  namespace: 'LexicalViewer',
  theme: editorTheme,
  nodes,
  editable: false,
  onError: (error: Error) => {
    console.error(error);
  },
};

interface LexicalViewerProps {
  serializedState: SerializedEditorState | null | undefined;
  className?: string;
}

export function LexicalViewer({ serializedState, className }: LexicalViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Find the scrollable parent
    const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
      let parent = element.parentElement;
      while (parent) {
        const overflow = window.getComputedStyle(parent).overflowY;
        if (overflow === 'auto' || overflow === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const handleWheel = (e: WheelEvent) => {
      const scrollableParent = findScrollableParent(container);
      if (scrollableParent) {
        e.preventDefault();
        scrollableParent.scrollTop += e.deltaY;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  if (!serializedState) {
    return null;
  }

  return (
    <div ref={containerRef}>
      <LexicalComposer
        initialConfig={{
          ...viewerConfig,
          editorState: JSON.stringify(serializedState),
        }}
      >
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={className || 'lexical-viewer prose prose-sm max-w-none focus:outline-none'}
            />
          }
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </LexicalComposer>
    </div>
  );
}
